import { makeAxiosClient } from 'lib/api/ssapi/secondStepClient'
import { domains } from 'config/env'
import { formatError } from 'utils/formatError'
import { isFlagOn, PUBLISHED_CONTENT_FLAG } from 'utils/featureFlags'
import { actions } from './reducer'
import {
  createAuthoredCourseStateKey,
  createCurriculumCourseStateKey,
} from './selectors'

const getClient = () => makeAxiosClient(domains.CURRICULUM_SERVICE)

const CANONICAL_COURSE_CODE_BY_PROGRAM = {
  k5SelDigital: {
    K: 'k_core_tier-1_siteEducator',
    '1': '1_core_tier-1_siteEducator',
    '2': '2_core_tier-1_siteEducator',
    '3': '3_core_tier-1_siteEducator',
    '4': '4_core_tier-1_siteEducator',
    '5': '5_core_tier-1_siteEducator',
    'k-5_training_tier1': 'k-5_core-training_tier-1_educator',
    'k-2_curriculum_tier2': 'k-2_core_tier-2_siteEducator',
    '3-5_curriculum_tier2': '3-5_core_tier-2_siteEducator',
  },
  mspDigital: {
    '6': '6_core_tier-1_siteEducator',
    '7': '7_core_tier-1_siteEducator',
    '8': '8_core_tier-1_siteEducator',
    '6-8_training_tier1': '6-8_core-training_tier-1_educator',
    '6-8_curriculum_tier2': '6-8_core_tier-2_siteEducator',
  },
  highSchool: {
    // HS content is authored/built in curriculum-service under the `mixed`
    // audience segment (not `siteEducator` like K-5/MS), so the canonical
    // course code must use the `_mixed` suffix to resolve the built structure.
    highschool: '9-12_core_tier-1_mixed',
  },
}

export const resolveCanonicalCourseCode = (contentGroupCode, courseCode) => {
  if (!contentGroupCode || !courseCode) return courseCode
  return (
    CANONICAL_COURSE_CODE_BY_PROGRAM?.[contentGroupCode]?.[courseCode] ||
    courseCode
  )
}

/**
 * `contentGroupCode` reaches these operations from several callers and is not
 * guaranteed to carry the canonical program-key casing (e.g. 'highschool'
 * instead of 'highSchool'). Map it back to the canonical key so the course-code
 * lookup and the outgoing `content_group_code` never disagree. Programs absent
 * from the table (e.g. 'dwb') pass through untouched.
 */
export const resolveCanonicalContentGroupCode = contentGroupCode => {
  if (typeof contentGroupCode !== 'string') return contentGroupCode
  return (
    Object.keys(CANONICAL_COURSE_CODE_BY_PROGRAM).find(
      key => key.toLowerCase() === contentGroupCode.toLowerCase(),
    ) || contentGroupCode
  )
}

/**
 * Fetch a single course tree from the curriculum service
 * GET /v1/audienced-course-versions endpoint (Mode B — fresh resolution).
 *
 * Replaces the legacy `@cfc/course-manager-sdk` getCourseTree call.
 * Audience inputs come from SSAPI via the bearer JWT (attached by
 * `makeAxiosClient`); the service expects query string params, not a body.
 *
 * Optional `includePlaceholders` is normalized to the string form ("true"/
 * "false") that curriculum-service's lenient boolean query parser expects.
 */
export const fetchCurriculumCourseTree = ({
  contentGroupCode,
  courseCode,
  siteId,
  resolutionMode = 'singleSite',
  includePlaceholders,
  audienceMapVersion,
}) => {
  return async dispatch => {
    const courseStateKey = createCurriculumCourseStateKey(
      contentGroupCode,
      courseCode,
      siteId,
    )
    dispatch(actions.fetchingCourseTree({ courseStateKey }))

    try {
      const client = getClient()
      const canonicalCourseCode = resolveCanonicalCourseCode(
        contentGroupCode,
        courseCode,
      )
      const params = {
        resolutionMode,
        contentGroupCode,
        courseCode: canonicalCourseCode,
        ...(siteId != null ? { siteId } : {}),
        ...(includePlaceholders != null
          ? { includePlaceholders: String(includePlaceholders) }
          : {}),
        ...(audienceMapVersion ? { audienceMapVersion } : {}),
        ...(!isFlagOn([PUBLISHED_CONTENT_FLAG]) ? { draftMode: 'true' } : {}),
      }

      const response = await client.get('/v1/audienced-course-versions', {
        params,
      })
      dispatch(
        actions.fetchCourseTreeSuccess({
          courseStateKey,
          data: response.data,
        }),
      )
    } catch (error) {
      dispatch(
        actions.fetchCourseTreeError({
          courseStateKey,
          error: formatError(error),
        }),
      )
    }
  }
}

/**
 * Fetches a course tree using params from a coursePointer returned by
 * GET /v1/content. The coursePointer.query may use either camelCase keys
 * (per the curriculum-service schema) or snake_case keys (legacy wire format);
 * both are handled here.
 *
 * @param {Object} options
 * @param {Object} options.coursePointer    - coursePointer from /v1/content response
 * @param {string} options.contentGroupCode - used as the state-key prefix (e.g. 'dwb')
 * @param {number|null} options.siteId      - active site id
 */
const fetchCourseTreeFromPointer = ({
  coursePointer,
  contentGroupCode,
  siteId,
}) => {
  return async dispatch => {
    const query = coursePointer?.query || {}

    // Normalise coursePointer query — handle camelCase (schema) and snake_case (wire)
    const courseCode = query.courseCode || query.course_code
    const contentGroupVersionId =
      query.contentGroupVersionId ?? query.content_group_version_id ?? null
    const programId = query.programId ?? query.program_id ?? null

    if (!courseCode) {
      console.error(
        '[fetchCourseTreeFromPointer] courseCode missing from coursePointer.query',
      )
      return
    }

    const courseStateKey = createCurriculumCourseStateKey(
      contentGroupCode,
      courseCode,
      siteId,
    )
    dispatch(actions.fetchingCourseTree({ courseStateKey }))

    try {
      const client = getClient()
      const params = {
        resolutionMode: 'singleSite',
        courseCode,
        ...(contentGroupVersionId != null
          ? { contentGroupVersionId }
          : { contentGroupCode }),
        ...(siteId != null ? { siteId } : {}),
        ...(programId != null ? { programId } : {}),
        ...(!isFlagOn([PUBLISHED_CONTENT_FLAG]) ? { draftMode: 'true' } : {}),
      }

      const response = await client.get('/v1/audienced-course-versions', {
        params,
      })
      dispatch(
        actions.fetchCourseTreeSuccess({
          courseStateKey,
          data: response.data,
        }),
      )
    } catch (error) {
      dispatch(
        actions.fetchCourseTreeError({
          courseStateKey,
          error: formatError(error),
        }),
      )
    }
  }
}

/**
 * Fetch the full authored course structure from the curriculum service
 * GET /v1/course-versions/tree endpoint.
 *
 * Unlike /v1/audienced-course-versions this returns every authored node rather
 * than the subset resolved for the caller's audience, so leaf node ids are
 * always present for mark-complete and completion-status lookups. There is no
 * audience or site input; `siteId` only participates in the cache key so
 * authored entries sit alongside audienced ones in the same slice.
 *
 * Query params are sent snake_case to match the endpoint's published schema.
 * `format=both` returns the recursive tree and the flat node list, and
 * available audiences are omitted since the authored structure is not
 * audience-filtered.
 */
export const fetchAuthoredCourseTree = ({
  contentGroupCode,
  courseCode,
  siteId,
  contentGroupVersionId,
}) => {
  return async dispatch => {
    // The cache key intentionally keeps the caller's raw `contentGroupCode`:
    // selectors build the same key from their own copy of it, so normalizing
    // here would leave the selector unable to find the entry it just fetched.
    const courseStateKey = createAuthoredCourseStateKey(
      contentGroupCode,
      courseCode,
      siteId,
    )
    dispatch(actions.fetchingCourseTree({ courseStateKey }))

    try {
      const client = getClient()
      const canonicalContentGroupCode = resolveCanonicalContentGroupCode(
        contentGroupCode,
      )
      const params = {
        course_code: resolveCanonicalCourseCode(
          canonicalContentGroupCode,
          courseCode,
        ),
        format: 'both',
        include_available_audiences: 'false',
        ...(!isFlagOn([PUBLISHED_CONTENT_FLAG]) ? { draft_mode: 'true' } : {}),
      }

      // A CGV pin takes precedence; otherwise the service resolves the latest
      // published (or draft) CGV from the content group code.
      if (contentGroupVersionId != null) {
        params.content_group_version_id = contentGroupVersionId
      } else if (canonicalContentGroupCode) {
        params.content_group_code = canonicalContentGroupCode
      }

      const response = await client.get('/v1/course-versions/tree', { params })
      dispatch(
        actions.fetchCourseTreeSuccess({
          courseStateKey,
          data: response.data,
        }),
      )
    } catch (error) {
      dispatch(
        actions.fetchCourseTreeError({
          courseStateKey,
          error: formatError(error),
        }),
      )
    }
  }
}

/**
 * Authored-structure counterpart to `fetchCourseTreeFromPointer`. Reads the
 * course code (and CGV pin, when present) out of a coursePointer returned by
 * GET /v1/content and fetches the authored tree for it.
 */
export const fetchAuthoredCourseTreeFromPointer = ({
  coursePointer,
  contentGroupCode,
  siteId,
}) => {
  return async dispatch => {
    const query = coursePointer?.query || {}
    const courseCode = query.courseCode || query.course_code

    if (!courseCode) {
      console.error(
        '[fetchAuthoredCourseTreeFromPointer] courseCode missing from coursePointer.query',
      )
      return
    }

    return dispatch(
      fetchAuthoredCourseTree({
        contentGroupCode,
        courseCode,
        siteId,
        contentGroupVersionId:
          query.contentGroupVersionId ?? query.content_group_version_id ?? null,
      }),
    )
  }
}

const clearErrors = () => {
  return async dispatch => {
    dispatch(actions.clearErrors())
  }
}

export default {
  fetchCurriculumCourseTree,
  fetchCourseTreeFromPointer,
  fetchAuthoredCourseTree,
  fetchAuthoredCourseTreeFromPointer,
  clearErrors,
}
